import React, { useState, useEffect, useRef, useCallback } from 'react';
import { jsPDF } from 'jspdf';
import { Loader2, FileText, ArrowRight, ArrowLeft, AlertCircle, CheckCircle2, PenLine, Trash2, MessageCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { getPropertyRules, savePropertyRules, saveTenantAgreement } from '../../api/services/pgTenantService';

export interface AgreementPdfData {
  pg_name: string;
  owner_name: string;
  tenant_name: string;
  tenant_phone: string;
  room_number: string;
  rent_amount: number;
  security_deposit: number;
  maintenance_charges: number;
  payment_due_day: number;
  notice_period_months: number;
  agreement_start_date: string;
  duration_months: number;
  rules: string;
  host_signature_data_url?: string;
  tenant_signature_data_url?: string;
}

export function buildAgreementPdf(data: AgreementPdfData): jsPDF {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = 210;
  const margin = 18;
  const contentW = pageW - margin * 2;
  let y = 0;

  doc.setFillColor(39, 104, 89);
  doc.rect(0, 0, pageW, 38, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(255, 255, 255);
  doc.text(data.pg_name.toUpperCase(), pageW / 2, 16, { align: 'center' });
  doc.setFontSize(12);
  doc.setTextColor(251, 189, 35);
  doc.text('RENTAL AGREEMENT', pageW / 2, 27, { align: 'center' });
  doc.setFontSize(8);
  doc.setTextColor(200, 230, 220);
  doc.text(`Generated on ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}`, pageW / 2, 35, { align: 'center' });
  y = 48;

  const sectionTitle = (title: string) => {
    doc.setFillColor(39, 104, 89);
    doc.rect(margin, y, contentW, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.text(title, margin + 3, y + 5);
    y += 11;
  };

  const twoCol = (l1: string, v1: string, l2: string, v2: string) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(l1, margin, y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text(v1, margin + 38, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(l2, margin + contentW / 2, y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text(v2, margin + contentW / 2 + 40, y);
    y += 7;
  };

  sectionTitle('PARTIES TO THE AGREEMENT');
  twoCol('PG Owner:', data.owner_name, 'Tenant:', data.tenant_name);
  twoCol('Property Name:', data.pg_name, 'Phone:', data.tenant_phone);
  y += 3;

  sectionTitle('ACCOMMODATION & TERM');
  const startDate = new Date(data.agreement_start_date);
  const endDate = new Date(startDate);
  endDate.setMonth(endDate.getMonth() + data.duration_months);
  const fmt = (d: Date) => d.toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
  twoCol('Room / Bed:', data.room_number, 'Duration:', `${data.duration_months} months`);
  twoCol('Start Date:', fmt(startDate), 'End Date:', fmt(endDate));
  y += 3;

  sectionTitle('FINANCIAL TERMS');
  const formatCurrency = (n: number) => `INR ${n.toLocaleString('en-IN')}`;
  const ordinal = (n: number) => { const s = ['th', 'st', 'nd', 'rd']; const v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };
  twoCol('Monthly Rent:', formatCurrency(data.rent_amount), 'Security Deposit:', formatCurrency(data.security_deposit));
  twoCol('Due Day:', `${ordinal(data.payment_due_day)} of every month`, 'Notice Period:', `${data.notice_period_months} month(s)`);
  twoCol('Maintenance (on vacating):', formatCurrency(data.maintenance_charges), '', '');
  y += 3;

  sectionTitle('PG RULES & CONDITIONS');
  doc.setFillColor(248, 250, 248);
  doc.setDrawColor(39, 104, 89);
  doc.setLineWidth(0.3);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(40, 40, 40);
  const rulesLines = doc.splitTextToSize(data.rules, contentW - 6);
  const rulesH = rulesLines.length * 4.5 + 6;
  doc.rect(margin, y, contentW, rulesH, 'FD');
  doc.text(rulesLines, margin + 3, y + 5);
  y += rulesH + 6;

  sectionTitle('SIGNATURES');
  y += 4;

  const sigBlock = (label: string, name: string, x: number, signatureDataUrl?: string) => {
    if (signatureDataUrl) {
      try {
        doc.addImage(signatureDataUrl, 'PNG', x, y + 2, 65, 16);
      } catch {}
    }
    doc.setDrawColor(150, 150, 150);
    doc.setLineWidth(0.4);
    doc.line(x, y + 18, x + 70, y + 18);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(30, 30, 30);
    doc.text(name, x, y + 23);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(label, x, y + 28);
    const dateStr = signatureDataUrl
      ? `Date: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`
      : 'Date: _______________';
    doc.text(dateStr, x, y + 33);
  };

  sigBlock('PG Owner / Landlord', data.owner_name, margin, data.host_signature_data_url);
  sigBlock('Tenant', data.tenant_name, margin + contentW / 2, data.tenant_signature_data_url);
  y += 40;

  doc.setFillColor(255, 248, 225);
  doc.setDrawColor(251, 189, 35);
  doc.setLineWidth(0.5);
  const noticeText = 'IMPORTANT NOTICE: This document is a draft agreement for reference purposes only. For legal validity, this agreement should be printed on stamp paper of appropriate value (as per state regulations), signed by both parties in the presence of two witnesses, and registered with the local Sub-Registrar office if the rental period exceeds 11 months.';
  const noticeLines = doc.splitTextToSize(noticeText, contentW - 6);
  const noticeH = noticeLines.length * 4.2 + 8;
  doc.rect(margin, y, contentW, noticeH, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(133, 100, 4);
  doc.text('IMPORTANT NOTICE', margin + 3, y + 5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(92, 69, 2);
  doc.text(noticeLines, margin + 3, y + 10);

  doc.setFontSize(7);
  doc.setTextColor(160, 160, 160);
  doc.text('Page 1 of 1', pageW / 2, 290, { align: 'center' });

  return doc;
}

interface AgreementDialogProps {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  propertyId: string;
  tenantName: string;
  tenantPhone: string;
  roomNumber: string;
  rent: number;
  advance: number;
}

interface RulesForm {
  pg_name: string;
  owner_name: string;
  rules: string;
}

interface AgreementForm {
  security_deposit: string;
  maintenance_charges: string;
  payment_due_day: string;
  notice_period_months: string;
  duration_months: string;
  agreement_start_date: string;
}

const PRIMARY = '#276859';
const SECONDARY = '#FBBD23';

const AgreementDialog: React.FC<AgreementDialogProps> = ({
  open, onClose, tenantId, propertyId, tenantName, tenantPhone, roomNumber, rent, advance,
}) => {
  const [step, setStep] = useState(0);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [hasDrawn, setHasDrawn] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);

  const [rules, setRules] = useState<RulesForm>({ pg_name: '', owner_name: '', rules: '' });
  const [agreement, setAgreement] = useState<AgreementForm>({
    security_deposit: String(advance || ''),
    maintenance_charges: '',
    payment_due_day: '5',
    notice_period_months: '1',
    duration_months: '11',
    agreement_start_date: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    if (open) {
      setStep(0);
      setError('');
      setHasDrawn(false);
      loadRules();
    }
  }, [open]);

  useEffect(() => {
    if (step === 2) {
      // Small delay to ensure canvas is in DOM
      setTimeout(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.strokeStyle = '#1a1a1a';
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasDrawn(false);
      }, 50);
    }
  }, [step]);

  async function loadRules() {
    setRulesLoading(true);
    try {
      const data = await getPropertyRules(propertyId);
      if (data && data.pg_name) {
        setRules({ pg_name: data.pg_name, owner_name: data.owner_name, rules: data.rules });
      }
    } catch (_) {}
    setRulesLoading(false);
  }

  async function handleSaveRules() {
    if (!rules.pg_name.trim() || !rules.owner_name.trim() || !rules.rules.trim()) {
      setError('All fields are required.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await savePropertyRules(propertyId, rules);
      setStep(1);
    } catch {
      setError('Failed to save PG rules. Please try again.');
    }
    setSaving(false);
  }

  function handleAdvanceToSign() {
    if (!agreement.security_deposit || !agreement.payment_due_day || !agreement.agreement_start_date) {
      setError('All fields are required.');
      return;
    }
    const dueDay = parseInt(agreement.payment_due_day);
    if (isNaN(dueDay) || dueDay < 1 || dueDay > 28) {
      setError('Rent due day must be between 1 and 28.');
      return;
    }
    setError('');
    setStep(2);
  }

  // Canvas drawing handlers
  const getMousePos = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    const scaleX = canvasRef.current!.width / rect.width;
    const scaleY = canvasRef.current!.height / rect.height;
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const getTouchPos = (e: React.TouchEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    const scaleX = canvasRef.current!.width / rect.width;
    const scaleY = canvasRef.current!.height / rect.height;
    const touch = e.touches[0];
    return { x: (touch.clientX - rect.left) * scaleX, y: (touch.clientY - rect.top) * scaleY };
  };

  const beginStroke = useCallback((x: number, y: number) => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    isDrawingRef.current = true;
    ctx.beginPath();
    ctx.moveTo(x, y);
  }, []);

  const continueStroke = useCallback((x: number, y: number) => {
    if (!isDrawingRef.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  }, []);

  const endStroke = useCallback(() => {
    isDrawingRef.current = false;
  }, []);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  }, []);

  async function handleGeneratePdf() {
    if (!hasDrawn) {
      setError('Please sign before generating the agreement.');
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const signatureDataUrl = canvas.toDataURL('image/png');

    const dueDay = parseInt(agreement.payment_due_day);
    setError('');
    setGenerating(true);
    try {
      await saveTenantAgreement(tenantId, {
        property_id: propertyId,
        pg_name: rules.pg_name,
        owner_name: rules.owner_name,
        room_number: roomNumber,
        rent_amount: rent,
        security_deposit: parseFloat(agreement.security_deposit),
        maintenance_charges: parseFloat(agreement.maintenance_charges) || 0,
        payment_due_day: dueDay,
        notice_period_months: parseInt(agreement.notice_period_months),
        agreement_start_date: agreement.agreement_start_date,
        duration_months: parseInt(agreement.duration_months),
        rules: rules.rules,
        host_signature: signatureDataUrl,
      });

      const phone = tenantPhone.startsWith('91') ? tenantPhone : `91${tenantPhone}`;
      const msg = `Hi ${tenantName}, your rental agreement for ${rules.pg_name} has been generated.\n\nPlease open the Social Bunk app, go to *My Info* → *Agreement* and tap *View & Download* to review and sign the agreement.\n\nYour signature is required to complete the agreement.\n— Your PG Host`;
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
      onClose();

    } catch {
      setError('Failed to save agreement. Please try again.');
    }
    setGenerating(false);
  }

  const inputClass = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#276859] focus:border-transparent";
  const labelClass = "block text-sm font-semibold text-[#276859] mb-1";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#276859] flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Generate Agreement
          </DialogTitle>
        </DialogHeader>

        {/* Step indicator — 3 steps */}
        <div className="flex items-center gap-2 py-2">
          <StepDot number={1} label="PG Rules" active={step === 0} done={step > 0} />
          <div className={`flex-1 h-0.5 ${step > 0 ? 'bg-[#FBBD23]' : 'bg-gray-200'}`} />
          <StepDot number={2} label="Agreement" active={step === 1} done={step > 1} />
          <div className={`flex-1 h-0.5 ${step > 1 ? 'bg-[#FBBD23]' : 'bg-gray-200'}`} />
          <StepDot number={3} label="Sign" active={step === 2} done={false} />
        </div>

        {error && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            {error}
          </div>
        )}

        {/* Step 1 — PG Rules */}
        {step === 0 && (
          rulesLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#276859]" />
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {rules.pg_name && (
                <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                  Existing PG rules loaded. Edit if needed.
                </div>
              )}

              <div>
                <label className={labelClass}>PG / Property Name</label>
                <input className={inputClass} placeholder="e.g. Green Valley PG" value={rules.pg_name}
                  onChange={e => setRules(r => ({ ...r, pg_name: e.target.value }))} />
              </div>
              <div>
                <label className={labelClass}>Owner Name</label>
                <input className={inputClass} placeholder="e.g. Ravi Kumar" value={rules.owner_name}
                  onChange={e => setRules(r => ({ ...r, owner_name: e.target.value }))} />
              </div>
              <div>
                <label className={labelClass}>PG Rules</label>
                <textarea className={inputClass} rows={7}
                  placeholder={"1. Visitors not allowed after 10 PM\n2. Rent due by the 5th of every month\n3. No smoking inside the premises"}
                  value={rules.rules}
                  onChange={e => setRules(r => ({ ...r, rules: e.target.value }))} />
              </div>

              <Button className="w-full bg-[#276859] hover:bg-[#1f5246] text-white font-semibold py-3"
                onClick={handleSaveRules} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {saving ? 'Saving…' : 'Save & Continue'}
                {!saving && <ArrowRight className="h-4 w-4 ml-2" />}
              </Button>
            </div>
          )
        )}

        {/* Step 2 — Agreement Details */}
        {step === 1 && (
          <div className="space-y-4 py-2">
            {/* Tenant summary */}
            <div className="rounded-xl p-4 text-white" style={{ background: 'linear-gradient(135deg, #276859, #2A5248)' }}>
              <p className="font-bold text-lg">{tenantName}</p>
              <p className="text-sm opacity-75">{tenantPhone}</p>
              <div className="flex gap-3 mt-2">
                <span className="bg-white/20 rounded-full px-3 py-1 text-xs">Room {roomNumber}</span>
                <span className="bg-white/20 rounded-full px-3 py-1 text-xs">₹{rent.toLocaleString('en-IN')}/mo</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Security Deposit (INR)</label>
                <input type="number" className={inputClass} placeholder="e.g. 10000"
                  value={agreement.security_deposit}
                  onChange={e => setAgreement(a => ({ ...a, security_deposit: e.target.value }))} />
              </div>
              <div>
                <label className={labelClass}>Maintenance Charges on Vacating (INR)</label>
                <input type="number" className={inputClass} placeholder="e.g. 500"
                  value={agreement.maintenance_charges}
                  onChange={e => setAgreement(a => ({ ...a, maintenance_charges: e.target.value }))} />
              </div>
              <div>
                <label className={labelClass}>Rent Due Day (1–28)</label>
                <input type="number" className={inputClass} placeholder="e.g. 5" min={1} max={28}
                  value={agreement.payment_due_day}
                  onChange={e => setAgreement(a => ({ ...a, payment_due_day: e.target.value }))} />
              </div>
              <div>
                <label className={labelClass}>Notice Period (months)</label>
                <input type="number" className={inputClass} placeholder="e.g. 1"
                  value={agreement.notice_period_months}
                  onChange={e => setAgreement(a => ({ ...a, notice_period_months: e.target.value }))} />
              </div>
              <div>
                <label className={labelClass}>Duration (months)</label>
                <input type="number" className={inputClass} placeholder="e.g. 11"
                  value={agreement.duration_months}
                  onChange={e => setAgreement(a => ({ ...a, duration_months: e.target.value }))} />
              </div>
            </div>

            <div>
              <label className={labelClass}>Agreement Start Date</label>
              <input type="date" className={inputClass}
                value={agreement.agreement_start_date}
                onChange={e => setAgreement(a => ({ ...a, agreement_start_date: e.target.value }))} />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              <strong>Note:</strong> The PDF is a draft agreement. For legal validity, print on stamp paper,
              sign in presence of two witnesses, and register if the period exceeds 11 months.
            </div>

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 border-[#276859] text-[#276859]"
                onClick={() => { setStep(0); setError(''); }}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button className="flex-[2] bg-[#276859] hover:bg-[#1f5246] text-white font-semibold"
                onClick={handleAdvanceToSign}>
                <PenLine className="h-4 w-4 mr-2" />
                Next: Sign Agreement
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3 — Host Signature */}
        {step === 2 && (
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
              <PenLine className="h-4 w-4 shrink-0" />
              Draw your signature. The tenant will be notified on WhatsApp to open the app and sign.
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className={labelClass}>Host Signature</label>
                <button
                  type="button"
                  onClick={clearCanvas}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-500 transition-colors"
                >
                  <Trash2 className="h-3 w-3" />
                  Clear
                </button>
              </div>
              <div className="border-2 border-dashed border-gray-300 rounded-xl overflow-hidden bg-gray-50 touch-none">
                <canvas
                  ref={canvasRef}
                  width={500}
                  height={140}
                  className="w-full cursor-crosshair"
                  style={{ touchAction: 'none' }}
                  onMouseDown={e => beginStroke(getMousePos(e).x, getMousePos(e).y)}
                  onMouseMove={e => continueStroke(getMousePos(e).x, getMousePos(e).y)}
                  onMouseUp={endStroke}
                  onMouseLeave={endStroke}
                  onTouchStart={e => { e.preventDefault(); beginStroke(getTouchPos(e).x, getTouchPos(e).y); }}
                  onTouchMove={e => { e.preventDefault(); continueStroke(getTouchPos(e).x, getTouchPos(e).y); }}
                  onTouchEnd={endStroke}
                />
              </div>
              {!hasDrawn && (
                <p className="text-xs text-gray-400 mt-1 text-center">Draw your signature in the box above</p>
              )}
            </div>

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 border-[#276859] text-[#276859]"
                onClick={() => { setStep(1); setError(''); }}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button
                className="flex-[2] bg-[#276859] hover:bg-[#1f5246] text-white font-semibold"
                onClick={handleGeneratePdf}
                disabled={generating}
              >
                {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <MessageCircle className="h-4 w-4 mr-2" />}
                {generating ? 'Generating…' : 'Generate & Notify Tenant'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const StepDot: React.FC<{ number: number; label: string; active: boolean; done: boolean }> = ({ number, label, active, done }) => (
  <div className="flex flex-col items-center">
    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm
      ${active ? 'bg-[#FBBD23] text-[#276859]' : done ? 'bg-[#FBBD23] text-[#276859]' : 'bg-gray-200 text-gray-400'}`}>
      {done ? <CheckCircle2 className="h-4 w-4" /> : number}
    </div>
    <span className={`text-xs mt-1 ${active ? 'text-[#276859] font-semibold' : 'text-gray-400'}`}>{label}</span>
  </div>
);

export default AgreementDialog;
